import { User } from '../../types/auth'
import type {
  AdminOrgFull,
  OrgsListResponse,
  OrgAccount,
  OrgMember,
  OrgFilterType,
  OrgType,
  OrgExchangeParticipant,
} from '../../types/branding'
import { apiRequest } from './client'

export type { AdminOrgFull, OrgsListResponse, OrgAccount, OrgMember, OrgFilterType, OrgType, OrgExchangeParticipant }

export interface SubProperty {
  name: string
  address: string
  value: number
  imageUrl?: string | null
}

export interface MarketplaceProperty {
  id: string
  orgId: string
  title: string
  description: string | null
  propertyType: string | null
  addressStreet: string | null
  addressCity: string | null
  addressState: string | null
  addressZip: string | null
  askingPriceCents: number | null
  capRate: number | null
  noi: number | null
  images: string[]
  published: boolean
  publishedAt: string | null
  listedBy: string | null
  dst: boolean
  sponsor: string | null
  dstMinInvestmentCents: number | null
  dstExpectedReturnMin: number | null
  dstExpectedReturnMax: number | null
  dstHoldPeriodMonths: number | null
  dstDistributionFrequency: 'monthly' | 'quarterly' | 'annually' | null
  dstAssetClass: string | null
  dstOfferingSizeCents: number | null
  qualifiedStatus: 'qualified' | 'pending_review' | 'unqualified'
  subProperties: SubProperty[]
  createdAt: string
  updatedAt: string
}

export interface MarketplacePropertyVersion {
  id: string
  propertyId: string
  versionNumber: number
  snapshot: MarketplaceProperty
  changedAt: string
  changeSummary: string | null
  changedBy: {
    id: string
    firstName: string | null
    lastName: string | null
    email: string | null
  } | null
}

export interface DstListing {
  id: string
  title: string
  description: string | null
  propertyType: string | null
  addressCity: string | null
  addressState: string | null
  askingPriceCents: number | null
  capRate: number | null
  noi: number | null
  images: string[]
  publishedAt: string | null
  sponsor: string | null
  dstMinInvestmentCents: number | null
  dstExpectedReturnMin: number | null
  dstExpectedReturnMax: number | null
  dstHoldPeriodMonths: number | null
  dstDistributionFrequency: 'monthly' | 'quarterly' | 'annually' | null
  dstAssetClass: string | null
  dstOfferingSizeCents: number | null
  qualifiedStatus: 'qualified' | 'pending_review' | 'unqualified'
  subProperties: SubProperty[]
  createdAt: string
}

export interface MarketplacePropertiesResponse {
  properties: MarketplaceProperty[]
  total: number
}

export interface MarketplacePropertyFilters {
  status?: string
  property_type?: string
  address_state?: string
  org_id?: string
}

export interface CreateMarketplacePropertyData {
  orgId: string
  title: string
  description?: string
  propertyType?: string
  addressStreet?: string
  addressCity?: string
  addressState?: string
  addressZip?: string
  askingPriceCents?: number
  capRate?: number
  noi?: number
  images?: string[]
  published?: boolean
  dst?: boolean
  sponsor?: string
  dstMinInvestmentCents?: number
  dstExpectedReturnMin?: number
  dstExpectedReturnMax?: number
  dstHoldPeriodMonths?: number
  dstDistributionFrequency?: 'monthly' | 'quarterly' | 'annually'
  dstAssetClass?: string
  dstOfferingSizeCents?: number
  qualifiedStatus?: 'qualified' | 'pending_review' | 'unqualified'
  subProperties?: SubProperty[]
}

export interface PlatformSettings {
  deadlineReminder45DayDays: number
  deadlineReminder180DayDays: number
  vectrWorkflowIdZillowEnrichment: string | null
  vectrWorkflowIdKycOrchestration: string | null
  vectrWorkflowIdEsignOrchestration: string | null
  vectrWorkflowIdOfacScreening: string | null
  emailFromAddress: string
  maintenanceMode: boolean
  demoResetEnabled: boolean
  updatedAt?: string
  updatedBy?: string | null
}

export interface AdminDocument {
  id: string
  name: string | null
  documentType: string | null
  mimeType: string | null
  sizeBytes: number | null
  status: 'pending' | 'approved' | 'rejected' | 'archived'
  uploadedBy: {
    id: string
    firstName: string
    lastName: string
    email: string
  } | null
  uploadedAt: string | null
  exchangeId: string | null
  orgId: string | null
  reviewerId: string | null
  reviewerNotes: string | null
  reviewedAt: string | null
}

export interface AdminDocumentsResponse {
  documents: AdminDocument[]
  total: number
}

export interface AdminDocumentFilters {
  org_id?: string
  exchange_id?: string
  status?: string
  document_type?: string
}

export interface AdminOrg {
  id: string
  name: string
  createdAt: string
}

export interface AdminOrgExchange {
  id: string
  name: string
  status: string
  createdAt: string
}

export interface AdminStats {
  totalUsers: number
  totalExchanges: number
  totalOrganizations: number
  verifiedUsers: number
  activeExchanges: number
  pendingVerifications: number
  kycComplete: number
}

export interface AdminUser extends User {
  exchangeCount?: number
  accountCount?: number
  mfaEnabled?: boolean
}

export type PlatformRole = 'platform_admin' | 'platform_support'
export type OrgRole = 'org_owner' | 'org_admin' | 'org_staff'

export type ExchangeRole = 'exchanger' | 'qi_officer' | 'advisor' | 'title_company' | 'attorney'

export interface ExchangeAccessEntry {
  exchangeId: string
  exchangeRole: ExchangeRole
}

export interface ExchangeAccessBody {
  exchangeId: string
  userType: 'owner' | 'accounting' | 'real_estate_agent' | 'attorney' | 'member' | 'viewer' | 'custom'
  featurePermissions?: string[]
}

export interface CreateUserBody {
  email: string
  firstName: string
  lastName: string
  platformRole?: PlatformRole | null
  orgId?: string | null
  orgRole?: OrgRole | null
  sendWelcomeEmail?: boolean
  exchangeAccess?: ExchangeAccessBody
}

export interface CreateUserResponse {
  user: AdminUser
  oneTimeCredentials?: { password: string }
}

export interface AdminUsersResponse {
  users: AdminUser[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AdminExchange {
  id: string
  accountId: string
  name: string
  status: string
  accountName?: string
  account?: {
    id: string
    name: string
  } | null
  owner?: {
    id: string
    email: string
    firstName: string
    lastName: string
  }
  userCount?: number
  createdAt: string
  updatedAt: string
}

export interface AdminExchangesResponse {
  exchanges: AdminExchange[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export const adminApi = {
  async getStats(): Promise<AdminStats> {
    return apiRequest<AdminStats>('/admin/stats')
  },

  async getUsers(params?: {
    search?: string
    page?: number
    limit?: number
  }): Promise<AdminUsersResponse> {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.limit) searchParams.set('limit', String(params.limit))
    const qs = searchParams.toString()
    return apiRequest<AdminUsersResponse>(`/admin/users${qs ? `?${qs}` : ''}`)
  },

  async getUser(userId: string): Promise<AdminUser & {
    exchanges?: AdminExchange[]
    orgMemberships?: Array<{ id: string; role: string; organizations: { id: string; name: string } | null }>
  }> {
    return apiRequest(`/admin/users/${userId}`)
  },

  async updateUser(
    userId: string,
    updates: Partial<User> & { exchangeAccess?: ExchangeAccessEntry[] }
  ): Promise<User> {
    const data = await apiRequest<{ user: User }>(`/admin/users/${userId}`, {
      method: 'PATCH',
      body: updates,
    })
    return data.user
  },

  async deleteUser(userId: string): Promise<void> {
    await apiRequest(`/admin/users/${userId}`, { method: 'DELETE' })
  },

  async resetDemoUser(userId: string): Promise<{ user: User; message: string }> {
    return apiRequest(`/admin/users/${userId}/reset-demo`, { method: 'POST' })
  },

  async getExchanges(params?: {
    search?: string
    status?: string
    page?: number
    limit?: number
  }): Promise<AdminExchangesResponse> {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.limit) searchParams.set('limit', String(params.limit))
    const qs = searchParams.toString()
    return apiRequest<AdminExchangesResponse>(`/admin/exchanges${qs ? `?${qs}` : ''}`)
  },

  async getExchange(exchangeId: string): Promise<AdminExchange & { account?: any; permissions?: any[] }> {
    return apiRequest(`/admin/exchanges/${exchangeId}`)
  },

  async updateExchange(exchangeId: string, updates: Partial<AdminExchange>): Promise<AdminExchange> {
    const data = await apiRequest<{ exchange: AdminExchange }>(`/admin/exchanges/${exchangeId}`, {
      method: 'PATCH',
      body: updates,
    })
    return data.exchange
  },

  async deleteExchange(exchangeId: string): Promise<void> {
    await apiRequest(`/admin/exchanges/${exchangeId}`, { method: 'DELETE' })
  },

  async getAccounts(): Promise<any[]> {
    const data = await apiRequest<{ accounts: any[] }>('/admin/accounts')
    return data.accounts
  },

  async getDocuments(filters?: AdminDocumentFilters): Promise<AdminDocumentsResponse> {
    const searchParams = new URLSearchParams()
    if (filters?.org_id) searchParams.set('org_id', filters.org_id)
    if (filters?.exchange_id) searchParams.set('exchange_id', filters.exchange_id)
    if (filters?.status) searchParams.set('status', filters.status)
    if (filters?.document_type) searchParams.set('document_type', filters.document_type)
    const qs = searchParams.toString()
    return apiRequest<AdminDocumentsResponse>(`/admin/documents${qs ? `?${qs}` : ''}`)
  },

  async updateDocument(
    documentId: string,
    fields: { status?: 'pending' | 'approved' | 'rejected' | 'archived'; reviewerNotes?: string }
  ): Promise<AdminDocument> {
    const data = await apiRequest<{ document: AdminDocument }>(`/admin/documents/${documentId}`, {
      method: 'PATCH',
      body: fields,
    })
    return data.document
  },

  async deleteDocument(documentId: string): Promise<void> {
    await apiRequest(`/admin/documents/${documentId}`, { method: 'DELETE' })
  },

  async getOrgs(): Promise<AdminOrg[]> {
    const data = await apiRequest<{ orgs: AdminOrg[] }>('/admin/orgs')
    return data.orgs
  },

  async getOrgExchanges(orgId: string): Promise<AdminOrgExchange[]> {
    const data = await apiRequest<{ exchanges: AdminOrgExchange[] }>(`/admin/orgs/${orgId}/exchanges`)
    return data.exchanges
  },

  async listOrgs(params?: {
    filter?: OrgFilterType
    q?: string
    cursor?: string
    limit?: number
  }): Promise<OrgsListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.filter === 'demos') searchParams.set('is_demo_tenant', 'true')
    if (params?.filter === 'customers') searchParams.set('is_demo_tenant', 'false')
    if (params?.q) searchParams.set('q', params.q)
    if (params?.cursor) searchParams.set('cursor', params.cursor)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    searchParams.set('count', 'true')
    const qs = searchParams.toString()
    return apiRequest<OrgsListResponse>(`/admin/orgs?${qs}`)
  },

  async getOrgById(orgId: string): Promise<AdminOrgFull> {
    const data = await apiRequest<{ org: AdminOrgFull }>(`/admin/orgs/${orgId}`)
    return data.org
  },

  async createOrg(body: {
    name: string
    slug: string
    orgType?: OrgType
    demoTenant?: boolean
    primaryColor?: string
    accentColor?: string
    website?: string
  }): Promise<AdminOrgFull> {
    const data = await apiRequest<{ org: AdminOrgFull }>('/admin/orgs', {
      method: 'POST',
      body,
    })
    return data.org
  },

  async patchOrg(orgId: string, body: {
    name?: string
    orgType?: OrgType
    primaryColor?: string
    accentColor?: string
    website?: string
    demoTenant?: boolean
    reason?: string
    legalName?: string | null
    ein?: string | null
    stateOfFormation?: string | null
    addressStreet?: string | null
    addressCity?: string | null
    addressState?: string | null
    addressZip?: string | null
    businessPhone?: string | null
    businessEmail?: string | null
    qiLicenseNumber?: string | null
    qiBondAmountCents?: number | null
    riaCrdNumber?: string | null
    authorizedSignerName?: string | null
    authorizedSignerTitle?: string | null
  }): Promise<AdminOrgFull> {
    const data = await apiRequest<{ org: AdminOrgFull }>(`/admin/orgs/${orgId}`, {
      method: 'PATCH',
      body,
    })
    return data.org
  },

  async seedDemoOrg(orgId: string, body?: {
    personas?: 'all' | string[]
    seedSampleExchange?: boolean
    reason?: string
  }): Promise<{
    org: AdminOrgFull | null
    seeded: { personas: unknown[]; exchange: unknown }
    oneTimeCredentials: { email: string; password: string }[]
  }> {
    return apiRequest(`/admin/orgs/${orgId}/seed-demo`, {
      method: 'POST',
      body: body ?? {},
    })
  },

  async deleteOrg(orgId: string): Promise<void> {
    await apiRequest(`/admin/orgs/${orgId}`, { method: 'DELETE' })
  },

  async getOrgAccounts(orgId: string): Promise<OrgAccount[]> {
    const data = await apiRequest<{ accounts: OrgAccount[] }>(`/admin/orgs/${orgId}/accounts`)
    return data.accounts
  },

  async createOrgAccount(orgId: string, body: { name: string; accountType?: string }): Promise<OrgAccount> {
    const data = await apiRequest<{ account: OrgAccount }>(`/admin/orgs/${orgId}/accounts`, {
      method: 'POST',
      body,
    })
    return data.account
  },

  async updateOrgAccount(orgId: string, accountId: string, body: { name?: string; accountType?: string }): Promise<OrgAccount> {
    const data = await apiRequest<{ account: OrgAccount }>(`/admin/orgs/${orgId}/accounts/${accountId}`, {
      method: 'PATCH',
      body,
    })
    return data.account
  },

  async deleteOrgAccount(orgId: string, accountId: string): Promise<void> {
    await apiRequest(`/admin/orgs/${orgId}/accounts/${accountId}`, { method: 'DELETE' })
  },

  async getOrgMembers(orgId: string): Promise<OrgMember[]> {
    const data = await apiRequest<{ members: OrgMember[] }>(`/admin/orgs/${orgId}/members`)
    return data.members
  },

  async inviteOrgMember(orgId: string, body: { email: string; role: string }): Promise<OrgMember> {
    const data = await apiRequest<{ member: OrgMember }>(`/admin/orgs/${orgId}/invite`, {
      method: 'POST',
      body,
    })
    return data.member
  },

  async updateOrgMembership(membershipId: string, body: {
    role?: string
    active?: boolean
    accountId?: string | null
  }): Promise<OrgMember> {
    const data = await apiRequest<{ member: OrgMember }>(`/admin/memberships/${membershipId}`, {
      method: 'PATCH',
      body,
    })
    return data.member
  },

  async deleteOrgMembership(membershipId: string): Promise<void> {
    await apiRequest(`/admin/memberships/${membershipId}`, { method: 'DELETE' })
  },

  async getOrgExchangeParticipants(orgId: string): Promise<OrgExchangeParticipant[]> {
    const data = await apiRequest<{ participants: OrgExchangeParticipant[] }>(
      `/admin/orgs/${orgId}/exchange-participants`
    )
    return data.participants
  },

  async getMarketplaceProperties(
    filters?: MarketplacePropertyFilters
  ): Promise<MarketplacePropertiesResponse> {
    const searchParams = new URLSearchParams()
    if (filters?.status) searchParams.set('status', filters.status)
    if (filters?.property_type) searchParams.set('property_type', filters.property_type)
    if (filters?.address_state) searchParams.set('address_state', filters.address_state)
    if (filters?.org_id) searchParams.set('org_id', filters.org_id)
    const qs = searchParams.toString()
    return apiRequest<MarketplacePropertiesResponse>(
      `/admin/marketplace/properties${qs ? `?${qs}` : ''}`
    )
  },

  async getMarketplaceProperty(id: string): Promise<MarketplaceProperty> {
    const data = await apiRequest<{ property: MarketplaceProperty }>(
      `/admin/marketplace/properties/${id}`
    )
    return data.property
  },

  async createMarketplaceProperty(
    propertyData: CreateMarketplacePropertyData
  ): Promise<MarketplaceProperty> {
    const response = await apiRequest<{ property: MarketplaceProperty }>(
      '/admin/marketplace/properties',
      { method: 'POST', body: propertyData }
    )
    return response.property
  },

  async updateMarketplaceProperty(
    id: string,
    propertyData: Partial<CreateMarketplacePropertyData>
  ): Promise<MarketplaceProperty> {
    const response = await apiRequest<{ property: MarketplaceProperty }>(
      `/admin/marketplace/properties/${id}`,
      { method: 'PATCH', body: propertyData }
    )
    return response.property
  },

  async deleteMarketplaceProperty(id: string): Promise<void> {
    await apiRequest(`/admin/marketplace/properties/${id}`, { method: 'DELETE' })
  },

  async toggleMarketplacePropertyActive(
    id: string,
    published: boolean
  ): Promise<MarketplaceProperty> {
    const response = await apiRequest<{ property: MarketplaceProperty }>(
      `/admin/marketplace/properties/${id}/active`,
      { method: 'PATCH', body: { published } }
    )
    return response.property
  },

  async getSettings(): Promise<PlatformSettings> {
    return apiRequest<PlatformSettings>('/admin/settings')
  },

  async updateSettings(partial: Partial<PlatformSettings>): Promise<PlatformSettings> {
    return apiRequest<PlatformSettings>('/admin/settings', {
      method: 'PATCH',
      body: partial,
    })
  },

  async impersonate(
    targetUserId: string,
    reason: string,
    orgId?: string
  ): Promise<{ jwt: string; sessionId: string; expiresAt: string }> {
    return apiRequest<{ jwt: string; sessionId: string; expiresAt: string }>(
      '/admin/impersonate',
      { method: 'POST', body: { targetUserId, orgId, reason } }
    )
  },

  async endImpersonation(): Promise<{ jwt: string }> {
    return apiRequest<{ jwt: string }>('/admin/impersonate/end', { method: 'POST' })
  },

  async createUser(body: CreateUserBody): Promise<CreateUserResponse> {
    return apiRequest<CreateUserResponse>('/admin/users', {
      method: 'POST',
      body,
    })
  },

  async sendPasswordReset(userId: string): Promise<void> {
    await apiRequest(`/admin/users/${userId}/password-reset`, { method: 'POST' })
  },

  async setUserPassword(userId: string, password: string): Promise<void> {
    await apiRequest(`/admin/users/${userId}/set-password`, {
      method: 'POST',
      body: { password },
    })
  },

  async getMarketplacePropertyVersions(id: string): Promise<MarketplacePropertyVersion[]> {
    const data = await apiRequest<{ versions: MarketplacePropertyVersion[] }>(
      `/admin/marketplace/properties/${id}/versions`
    )
    return data.versions
  },

  async getMarketplacePropertyVersion(id: string, version: number): Promise<MarketplacePropertyVersion> {
    const data = await apiRequest<{ version: MarketplacePropertyVersion }>(
      `/admin/marketplace/properties/${id}/versions/${version}`
    )
    return data.version
  },

  async restoreMarketplacePropertyVersion(
    id: string,
    version: number
  ): Promise<{ property: MarketplaceProperty; restoredToVersion: number }> {
    return apiRequest(
      `/admin/marketplace/properties/${id}/versions/${version}/restore`,
      { method: 'POST' }
    )
  },

  async uploadMarketplaceImage(propertyId: string, file: File): Promise<{ images: string[] }> {
    return apiRequest<{ images: string[] }>(
      `/admin/marketplace/properties/${propertyId}/images`,
      { method: 'POST', body: file, headers: { 'Content-Type': file.type } }
    )
  },

  async deleteMarketplaceImage(propertyId: string, url: string): Promise<{ images: string[] }> {
    return apiRequest<{ images: string[] }>(
      `/admin/marketplace/properties/${propertyId}/images`,
      { method: 'DELETE', body: { url } }
    )
  },

  async seedMarketplaceSamples(orgId?: string): Promise<{
    org_id: string;
    listings: DstListing[];
    seeded_count: number;
    skipped_count: number;
  }> {
    return apiRequest('/admin/marketplace/seed-samples', {
      method: 'POST',
      body: orgId ? { orgId } : {},
    })
  },

  async resetExchangeDemoState(exchangeId: string): Promise<void> {
    await apiRequest(`/admin/exchanges/${exchangeId}/reset-demo-state`, { method: 'POST' })
  },

  async extendDemo(orgId: string, body: { until: string }): Promise<AdminOrgFull> {
    const data = await apiRequest<{ org: AdminOrgFull }>(`/admin/orgs/${orgId}/demo/extend`, {
      method: 'POST',
      body,
    })
    return data.org
  },

  async recordAccess(orgId: string): Promise<{ accessCount: number | null; lastAccessedAt: string | null }> {
    return apiRequest<{ accessCount: number | null; lastAccessedAt: string | null }>(
      `/admin/orgs/${orgId}/access`,
      { method: 'POST' },
    )
  },
}
